import { ExternalLink, Loader2, PencilIcon, Plus, Search, Trash, X, CheckIcon, XIcon } from 'lucide-react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { IsCreatingCtx } from '../../context';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useDebounce } from '@/hooks/use-debounce';
import { CustomPagination } from '@/pages/dashboard/components/custom-pagination';
import { ConfirmDialog } from '@/pages/dashboard/outlet/monitor/components/confirm-delete-dialog';
import { UserRole } from '@/constant';
import { DateRange } from 'react-day-picker';

import CreateShellUrls from './components/create-shell-url-dialog';
import EditUrlDialog from './components/edit-url-dialog';
import DateRangePicker from '../../components/date-range-picker';
import { toast } from 'sonner';

interface Link {
  link: string;
  team: string;
  status: 'ACTIVE' | 'INACTIVE';
  password: string;
  username?: string;
  hacker_name?: string;
  deleted: boolean;
  createdAt?: string;
}

interface LinkResponse {
  message: string;
  data: {
    all_link_data: Link[];
    total: {
      data: number;
      page: number;
    };
  };
}

const AdminMonitor: React.FC = () => {
  const [links, setLinks] = useState<Link[]>([]);
  const [totalLinks, setTotalLinks] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingLinks, setUpdatingLinks] = useState<Set<string>>(new Set());
  const [userRole, setUserRole] = useState<string | null>(null);

  const { isCreating, setIsCreating } = useContext(IsCreatingCtx);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const debouncedStatus = useDebounce(statusFilter, 500);
  const [teamFilter, setTeamFilter] = useState<string>('');
  const debouncedTeam = useDebounce(teamFilter, 500);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const debouncedDateRange = useDebounce(dateRange, 500);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [linkToEdit, setLinkToEdit] = useState<Link | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const [editingPassword, setEditingPassword] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const [editingUsername, setEditingUsername] = useState<string | null>(null);
  const [newUsername, setNewUsername] = useState('');

  const toggleCreateModal = () => {
    setIsCreating(!isCreating);
  };

  const handleClearSearch = () => {
    setSearch('');
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setTeamFilter('');
    setDateRange(undefined);
    setCurrentPage(1);
  };

  const fetchLinks = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());

      if (debouncedSearch) params.append('search', debouncedSearch);
      if (debouncedStatus) params.append('status', debouncedStatus);
      if (debouncedTeam) params.append('team', debouncedTeam);
      if (debouncedDateRange?.from) params.append('startDate', debouncedDateRange.from.toISOString());
      if (debouncedDateRange?.to) params.append('endDate', debouncedDateRange.to.toISOString());

      const response = await fetch(`/api/v1/link/?${params.toString()}`);
      const data: LinkResponse = await response.json();

      setLinks(data.data.all_link_data);
      setTotalLinks(data.data.total.data);
      setTotalPages(data.data.total.page);
    } catch (error) {
      toast.error('Error fetching links');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearch, debouncedStatus, debouncedTeam, debouncedDateRange]);

  const handleDeleteLink = async () => {
    if (!linkToDelete) return;

    setIsDeleting(true);
    setUpdatingLinks(prev => new Set(prev).add(linkToDelete));

    try {
      const response = await fetch('/api/v1/link/', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ link: linkToDelete }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete link');
      }

      await fetchLinks();
    } catch (error) {
      toast.error('Error deleting link');
    } finally {
      setIsDeleting(false);
      setUpdatingLinks(prev => {
        const newSet = new Set(prev);
        newSet.delete(linkToDelete);
        return newSet;
      });
      setLinkToDelete(null);
    }
  };

  const openDeleteDialog = (link: string) => {
    setLinkToDelete(link);
    setDeleteDialogOpen(true);
  };

  const openEditDialog = (link: Link) => {
    setLinkToEdit(link);
    setEditDialogOpen(true);
  };

  const handleUpdateLink = async (updatedLink: Link) => {
    setIsEditing(true);
    setUpdatingLinks(prev => new Set(prev).add(updatedLink.link));

    try {
      const response = await fetch('/api/v1/link/', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedLink),
      });

      if (!response.ok) {
        throw new Error('Failed to update link');
      }

      await fetchLinks();
    } catch (error) {
      toast.error('Error updating link');
    } finally {
      setIsEditing(false);
      setUpdatingLinks(prev => {
        const newSet = new Set(prev);
        newSet.delete(updatedLink.link);
        return newSet;
      });
    }
  };

  const handleUpdatePassword = async (link: Link, password: string) => {
    if (password === link.password) {
      setEditingPassword(null);
      return;
    }

    setUpdatingLinks(prev => new Set(prev).add(link.link));
    try {
      const updatedLink = { ...link, password };
      await handleUpdateLink(updatedLink);
    } finally {
      setEditingPassword(null);
    }
  };

  const handleUpdateUsername = async (link: Link, username: string) => {
    if (username === link.username) {
      setEditingUsername(null);
      return;
    }

    setUpdatingLinks(prev => new Set(prev).add(link.link));
    try {
      const updatedLink = { ...link, username };
      await handleUpdateLink(updatedLink);
    } finally {
      setEditingUsername(null);
    }
  };

  const handleToggleStatus = async (link: Link) => {
    if (userRole === UserRole.USER) {
      setUpdatingLinks(prev => new Set(prev).add(link.link));

      try {
        const response = await fetch('/api/v1/link/toggle-active', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            link_name: link.link,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to toggle link status');
        }

        await fetchLinks();
      } catch (error) {
        toast.error('Failed to toggle link status');
      } finally {
        setUpdatingLinks(prev => {
          const newSet = new Set(prev);
          newSet.delete(link.link);
          return newSet;
        });
      }
    } else {
      const newStatus: 'ACTIVE' | 'INACTIVE' = link.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      const updatedLink = { ...link, status: newStatus };
      await handleUpdateLink(updatedLink);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  useEffect(() => {
    const role = localStorage.getItem('role');
    setUserRole(role);
  }, []);

  const canViewActions = userRole === UserRole.MASTER || userRole === UserRole.HACKER;
  const canCreateShells = userRole === UserRole.MASTER;
  const canEditShells = userRole === UserRole.MASTER || userRole === UserRole.HACKER;
  const canChangeStatus = userRole === UserRole.MASTER || userRole === UserRole.HACKER || userRole === UserRole.USER;

  const truncateUrl = (url: string, maxLength: number = 60) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  return (
    <div onClick={() => setIsCreating(false)} className='space-y-4'>
      <Card className='border-primary/20 shadow-sm shadow-primary/10 dark:bg-gray-900 dark:border-gray-800 dark:shadow-md'>
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-4 border-b border-primary/20 dark:border-gray-800'>
          <CardTitle className='text-xl font-bold'>Shell Management</CardTitle>
          {canCreateShells && (
            <Button
              variant='default'
              size='icon'
              onClick={e => {
                e.stopPropagation();
                toggleCreateModal();
              }}
            >
              <Plus className='h-5 w-5' />
            </Button>
          )}
        </CardHeader>
        <CardContent className='pt-4 space-y-4'>
          <div className='flex flex-wrap gap-4'>
            <div className='relative w-full sm:w-64'>
              <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
              <Input
                placeholder='Search URLs...'
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className='pl-8 pr-8'
              />
              {search && (
                <Button variant='ghost' size='icon' className='absolute right-1 top-1.5 h-6 w-6' onClick={handleClearSearch}>
                  <X className='h-4 w-4' />
                </Button>
              )}
            </div>

            <Select
              value={statusFilter}
              onValueChange={value => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className='w-full sm:w-40'>
                <SelectValue placeholder='Status' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='ACTIVE'>Active</SelectItem>
                <SelectItem value='INACTIVE'>Inactive</SelectItem>
              </SelectContent>
            </Select>

            <div className='relative w-full sm:w-64'>
              <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
              <Input
                placeholder='Search Team...'
                value={teamFilter}
                onChange={e => {
                  setTeamFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className='pl-8 pr-8'
              />
              {teamFilter && (
                <Button
                  variant='ghost'
                  size='icon'
                  className='absolute right-1 top-1.5 h-6 w-6'
                  onClick={() => setTeamFilter('')}
                >
                  <X className='h-4 w-4' />
                </Button>
              )}
            </div>

            <DateRangePicker
              dateRange={dateRange}
              onDateRangeChange={range => {
                setDateRange(range);
                setCurrentPage(1);
              }}
              className='w-full sm:w-64'
            />

            <Button
              variant='outline'
              className='flex items-center justify-center hover:text-destructive'
              onClick={handleClearFilters}
            >
              <X className='mr-2 h-4 w-4' />
              Clear Filters
            </Button>
          </div>

          {canCreateShells && (
            <CreateShellUrls
              isCreating={isCreating}
              setIsCreating={setIsCreating}
              onShellCreated={() => {
                fetchLinks();
              }}
            />
          )}

          {isLoading ? (
            <div className='flex justify-center items-center py-12'>
              <Loader2 className='h-8 w-8 animate-spin text-primary' />
            </div>
          ) : links.length === 0 ? (
            <div className='rounded-md border border-primary/20 dark:border-gray-800 dark:bg-gray-900'>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={canViewActions ? 8 : 7} className='text-center py-12 text-muted-foreground'>
                      <div className='flex flex-col items-center gap-2'>
                        <div className='text-lg font-medium'>No shells found</div>
                        <div className='text-sm'>Try adjusting your search filters</div>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          ) : (
            <>
              <div className='rounded-md border border-primary/20 dark:border-gray-800 dark:bg-gray-900 overflow-hidden'>
                <div className='overflow-x-auto'>
                  <Table>
                    <TableHeader className='bg-primary/5 dark:bg-gray-800'>
                      <TableRow className='hover:bg-primary/5 dark:hover:bg-gray-800'>
                        <TableHead className='font-bold text-foreground dark:text-foreground w-[8%]'>Date</TableHead>
                        <TableHead className='font-bold text-foreground dark:text-foreground min-w-[350px] lg:w-[40%]'>
                          Shell URLs
                        </TableHead>
                        <TableHead className='font-bold text-foreground dark:text-foreground w-[12%]'>Username</TableHead>
                        <TableHead className='font-bold text-foreground dark:text-foreground w-[12%]'>Password</TableHead>
                        <TableHead className='font-bold text-foreground dark:text-foreground text-center w-[8%]'>Team</TableHead>
                        <TableHead className='font-bold text-foreground dark:text-foreground text-center w-[8%]'>
                          Hacker
                        </TableHead>
                        <TableHead className='font-bold text-foreground dark:text-foreground text-center w-[8%]'>
                          Status
                        </TableHead>
                        {canViewActions && (
                          <TableHead className='font-bold text-foreground dark:text-foreground text-center w-[10%]'>
                            Actions
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody className='dark:bg-gray-900'>
                      {links.map((link, index) => (
                        <TableRow
                          key={index}
                          className={`${link.deleted ? 'opacity-50' : ''} ${
                            updatingLinks.has(link.link)
                              ? 'bg-blue-50/50 dark:bg-blue-900/20 border-l-4 border-l-blue-400 dark:border-l-blue-500'
                              : 'hover:bg-muted/50 dark:hover:bg-gray-800/50'
                          } transition-colors duration-200`}
                        >
                          <TableCell className='w-[8%]'>
                            <span className='text-sm'>
                              {link.createdAt
                                ? new Date(link.createdAt).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                  })
                                : '-'}
                            </span>
                          </TableCell>
                          <TableCell className='font-medium min-w-[350px] lg:w-[40%] pr-4'>
                            <div className='flex items-center gap-2 max-w-full'>
                              <a
                                href={link.link}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='hover:underline flex items-center gap-2 min-w-0 flex-1 group'
                                onClick={e => e.stopPropagation()}
                                title={link.link}
                              >
                                <span className='truncate text-sm'>{truncateUrl(link.link, canViewActions ? 70 : 90)}</span>
                                <ExternalLink className='text-primary h-4 w-4 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity' />
                              </a>
                            </div>
                          </TableCell>
                          <TableCell className='w-[12%]'>
                            <div className='flex items-center justify-start gap-2'>
                              {editingUsername === link.link ? (
                                <div className='flex items-center gap-2 w-full'>
                                  <Input
                                    value={newUsername}
                                    onChange={e => setNewUsername(e.target.value)}
                                    className='w-28 h-8 text-sm'
                                    autoFocus
                                    onClick={e => e.stopPropagation()}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') {
                                        handleUpdateUsername(link, newUsername);
                                      } else if (e.key === 'Escape') {
                                        setEditingUsername(null);
                                      }
                                    }}
                                  />
                                  <Button
                                    size='sm'
                                    className='h-8 px-2'
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleUpdateUsername(link, newUsername);
                                    }}
                                  >
                                    <CheckIcon className='h-3 w-3' />
                                  </Button>
                                  <Button
                                    variant='outline'
                                    size='sm'
                                    className='h-8 px-2'
                                    onClick={e => {
                                      e.stopPropagation();
                                      setEditingUsername(null);
                                    }}
                                  >
                                    <XIcon className='h-3 w-3' />
                                  </Button>
                                </div>
                              ) : (
                                <span
                                  className={
                                    'px-3 py-1.5 rounded-full bg-primary/10 text-primary cursor-pointer hover:bg-primary/20 dark:bg-primary/20 dark:text-primary-foreground dark:hover:bg-primary/30 text-sm transition-colors inline-block max-w-full truncate' +
                                    (updatingLinks.has(link.link) ? ' opacity-75 cursor-not-allowed' : '')
                                  }
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (!link.deleted && !updatingLinks.has(link.link) && canChangeStatus) {
                                      setEditingUsername(link.link);
                                      setNewUsername(link.username || '');
                                    }
                                  }}
                                  title={link.username || 'No username'}
                                >
                                  {link.username
                                    ? link.username.length > 12
                                      ? link.username.substring(0, 12) + '...'
                                      : link.username
                                    : '-'}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className='w-[12%]'>
                            <div className='flex items-center justify-start gap-2'>
                              {editingPassword === link.link ? (
                                <div className='flex items-center gap-2 w-full'>
                                  <Input
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    className='w-28 h-8 text-sm'
                                    autoFocus
                                    onClick={e => e.stopPropagation()}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') {
                                        handleUpdatePassword(link, newPassword);
                                      } else if (e.key === 'Escape') {
                                        setEditingPassword(null);
                                      }
                                    }}
                                  />
                                  <Button
                                    size='sm'
                                    className='h-8 px-2'
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleUpdatePassword(link, newPassword);
                                    }}
                                  >
                                    <CheckIcon className='h-3 w-3' />
                                  </Button>
                                  <Button
                                    variant='outline'
                                    size='sm'
                                    className='h-8 px-2'
                                    onClick={e => {
                                      e.stopPropagation();
                                      setEditingPassword(null);
                                    }}
                                  >
                                    <XIcon className='h-3 w-3' />
                                  </Button>
                                </div>
                              ) : (
                                <span
                                  className={
                                    'px-3 py-1.5 rounded-full bg-primary/10 text-primary cursor-pointer hover:bg-primary/20 dark:bg-primary/20 dark:text-primary-foreground dark:hover:bg-primary/30 text-sm transition-colors inline-block max-w-full truncate' +
                                    (updatingLinks.has(link.link) ? ' opacity-75 cursor-not-allowed' : '')
                                  }
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (!link.deleted && !updatingLinks.has(link.link) && canChangeStatus) {
                                      setEditingPassword(link.link);
                                      setNewPassword(link.password || '');
                                    }
                                  }}
                                  title={link.password || 'No password'}
                                >
                                  {link.password
                                    ? link.password.length > 12
                                      ? link.password.substring(0, 12) + '...'
                                      : link.password
                                    : '-'}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className='text-center w-[8%]'>
                            <span className='text-sm font-medium'>{link.team}</span>
                          </TableCell>
                          <TableCell className='text-center w-[8%]'>
                            <span className='text-sm'>{link.hacker_name || '-'}</span>
                          </TableCell>
                          <TableCell className='text-center w-[8%]'>
                            <div className='flex justify-center'>
                              {updatingLinks.has(link.link) ? (
                                <div className='flex items-center gap-2'>
                                  <Loader2 className='h-4 w-4 animate-spin text-blue-500' />
                                  <span className='text-xs text-blue-600 dark:text-blue-400 font-medium'>Updating</span>
                                </div>
                              ) : (
                                <span
                                  className={`px-3 py-1.5 rounded-full border text-sm font-medium cursor-pointer transition-all duration-200 ${
                                    link.status === 'ACTIVE'
                                      ? 'border-green-400 bg-green-100 text-green-700 hover:bg-green-200 dark:border-green-500 dark:bg-green-500/20 dark:text-green-400 dark:hover:bg-green-500/30'
                                      : 'border-red-400 bg-red-100 text-red-700 hover:bg-red-200 dark:border-red-500 dark:bg-red-500/20 dark:text-red-400 dark:hover:bg-red-500/30'
                                  }`}
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (!link.deleted && !updatingLinks.has(link.link) && canChangeStatus) {
                                      handleToggleStatus(link);
                                    }
                                  }}
                                >
                                  {link.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                                </span>
                              )}
                            </div>
                          </TableCell>

                          {canViewActions && (
                            <TableCell className='text-center w-[10%]'>
                              <div className='flex space-x-1 justify-center'>
                                {canEditShells && (
                                  <>
                                    <Button
                                      className='hover:bg-primary/20 dark:hover:bg-primary/20'
                                      variant='secondary'
                                      size='sm'
                                      onClick={e => {
                                        e.stopPropagation();
                                        openEditDialog(link);
                                      }}
                                      disabled={updatingLinks.has(link.link)}
                                      title='Edit'
                                    >
                                      <PencilIcon className='h-4 w-4' />
                                    </Button>
                                    <Button
                                      className='bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-500/20 dark:text-red-400 dark:hover:bg-red-500/30 dark:border-red-500/40'
                                      variant='outline'
                                      size='sm'
                                      disabled={link.deleted || updatingLinks.has(link.link)}
                                      onClick={e => {
                                        e.stopPropagation();
                                        openDeleteDialog(link.link);
                                      }}
                                      title={link.deleted ? 'Deleted' : 'Delete'}
                                    >
                                      {updatingLinks.has(link.link) && linkToDelete === link.link ? (
                                        <Loader2 className='h-4 w-4 animate-spin' />
                                      ) : (
                                        <Trash className='h-4 w-4' />
                                      )}
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalLinks}
                onPageChange={setCurrentPage}
                itemsPerPage={20}
                showItemCount={true}
              />
            </>
          )}
        </CardContent>
      </Card>

      {canEditShells && (
        <>
          <ConfirmDialog
            isOpen={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            onConfirm={handleDeleteLink}
            linkToDelete={linkToDelete ?? undefined}
            isLoading={isDeleting}
          />

          {linkToEdit && (
            <EditUrlDialog
              isOpen={editDialogOpen}
              onClose={() => {
                setEditDialogOpen(false);
                setLinkToEdit(null);
              }}
              linkToEdit={linkToEdit}
              isLoading={isEditing}
              onConfirm={(updatedLink: Link) => {
                handleUpdateLink(updatedLink);
                setEditDialogOpen(false);
                setLinkToEdit(null);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AdminMonitor;
